/** @jsxImportSource preact */
import { useLayoutEffect, useRef } from "preact/hooks";

import type { ResolvedWidgetConfig } from "../config";
import * as m from "../paraglide/messages.js";
import type { ProactivePrompt } from "../proactive/proactive";
import { WIDGET_KEYFRAMES } from "./animations";
import { CloseIcon, SpinnerIcon } from "./icons";
import {
  headerButtonStyle,
  LauncherCapsule,
  launcherBubbleStyle,
  proactiveLauncherPrompt,
} from "./launcher-capsule";
import { deriveTheme } from "./theme";
import { useLauncherChrome } from "./use-launcher-chrome";
import { characterVariantFor } from "./widget-character";

/** 聊天资源加载中的等待卡片：点了立即出现、可关；失败给重试。 */
export interface ChatLoadingCard {
  phase: "loading" | "failed";
  onRetry: () => void;
  onClose: () => void;
}

interface LauncherRootProps {
  config: ResolvedWidgetConfig;
  prompt?: ProactivePrompt;
  card?: ChatLoadingCard;
  /**
   * 恢复「开着」的会话时入口先隐身：收起的角色画出来再被聊天窗换掉是
   * 一次闪烁。聊天资源失败或超时由外层撤掉这个标记，入口再现身。
   */
  dormant?: boolean;
  /** 悬停、触摸或聚焦入口都是打开意图：推进在途的聊天资源加载。 */
  onIntent?: () => void;
  onOpen: () => void;
}

/**
 * 入口阶段的根组件：只画角色入口、主动提示和等待卡片。
 * 完整聊天窗的资源到位后由 mountChat 接管，这里被整棵换掉。
 */
export function LauncherRoot({
  config,
  prompt,
  card,
  dormant = false,
  onIntent,
  onOpen,
}: LauncherRootProps) {
  const chrome = useLauncherChrome(config);
  const {
    mobile,
    viewport,
    side,
    launcherBottom,
    draggable,
    launcherDrag,
    horizontalSpacing,
  } = chrome;
  const appearance = chrome.placed.appearance;
  const theme = deriveTheme(appearance);
  const entryCharacter = characterVariantFor(
    appearance.character ?? "solid",
    theme.accent,
  );
  const glassBubble = theme.glass || entryCharacter === "watercolor";
  const radius = appearance.radius ?? 8;
  // 与完整聊天窗共用同一份换算：主动提示不播报，播报只给回复提示。
  const launcherPrompt =
    prompt && proactiveLauncherPrompt(prompt, m.widget_proactive_dismiss());
  const shellRef = useRef<HTMLDivElement>(null);
  const lastFocused = useRef<HTMLElement | null>(null);
  // 卡片的关闭、重试点完自己会消失，焦点掉到 body；焦点原来在壳里且那个
  // 节点已经拔掉时才还给入口按钮——买家正常把焦点挪出挂件时不能拽回来。
  useLayoutEffect(() => {
    const hostRoot = shellRef.current?.getRootNode();
    const active =
      hostRoot instanceof ShadowRoot ? hostRoot.activeElement : null;
    const previous = lastFocused.current;
    if (active === null && previous !== null && !previous.isConnected)
      shellRef.current
        ?.querySelector<HTMLElement>(".gn-launcher-toggle")
        ?.focus();
    lastFocused.current = active instanceof HTMLElement ? active : null;
  });
  const bodyPrompt =
    launcherPrompt !== undefined && appearance.proactivePromptStyle === "body";
  const capsule = (
    <LauncherCapsule
      position={side}
      size={launcherPrompt ? "medium" : "small"}
      open={false}
      present={false}
      prompt={launcherPrompt}
      bodyPrompt={bodyPrompt}
      drag={draggable ? launcherDrag : undefined}
      launcherLabel={appearance.launcherLabel ?? m.widget_launcher_label()}
      onOpen={onOpen}
      character={entryCharacter}
      motion={
        card === undefined
          ? launcherPrompt
            ? "quiet"
            : "idle"
          : card.phase === "failed"
            ? "blocked"
            : "working"
      }
      theme={theme}
      characterRadius={`${String(Math.min(radius * 1.8, 20))}px`}
      promptRadius={`${String(Math.min(radius * 1.8, 28))}px`}
      glassBubble={glassBubble}
    >
      {card && (
        <PendingCard
          card={card}
          theme={theme}
          glassBubble={glassBubble}
          alignRight={side !== "left"}
        />
      )}
    </LauncherCapsule>
  );
  // 休眠态什么都不画：聊天窗在路上，收起的角色露头一帧也是闪烁。
  if (dormant) return null;
  return (
    <div
      ref={shellRef}
      class="gonovus-widget"
      onFocusIn={onIntent}
      onPointerDown={onIntent}
      onPointerOver={onIntent}
      style={{
        position: "fixed",
        zIndex: 1000,
        ...(mobile
          ? {
              top: viewport.top,
              left: viewport.left,
              // 零宽外壳让露出的店面自然接收触摸。
              width: 0,
              "--gn-viewport-width": `${String(viewport.width)}px`,
              height: viewport.drawerHeight,
            }
          : {
              bottom: `max(${String(launcherBottom)}px, env(safe-area-inset-bottom))`,
              [side]: horizontalSpacing,
            }),
      }}
    >
      {/* 关键帧跟着组件走：放 shadowRoot 直下会多出一个子节点，卸载时也要另外清。 */}
      <style>{WIDGET_KEYFRAMES}</style>
      {mobile ? (
        <div
          class="gn-mobile-entry"
          data-open={false}
          style={{
            position: "fixed",
            bottom: `max(${String(launcherBottom)}px, env(safe-area-inset-bottom))`,
            [side]: horizontalSpacing,
          }}
        >
          {capsule}
        </div>
      ) : (
        capsule
      )}
    </div>
  );
}

/**
 * 等待卡片：占主动提示的位置，用同一壳样式。
 * 加载中不接收输入；失败后重试和关闭都摆在明面上。
 */
function PendingCard({
  card,
  theme,
  glassBubble,
  alignRight,
}: {
  card: ChatLoadingCard;
  theme: ReturnType<typeof deriveTheme>;
  glassBubble: boolean;
  alignRight: boolean;
}) {
  const failed = card.phase === "failed";
  return (
    <div
      class="gn-proactive-bubble"
      role={failed ? "alert" : "status"}
      style={{
        ...launcherBubbleStyle(theme, glassBubble, alignRight),
        alignItems: "center",
        gap: 4,
        padding: "6px 6px 6px 14px",
        fontSize: 13,
        lineHeight: 1.45,
      }}
    >
      {!failed && (
        <span
          aria-hidden="true"
          style={{ display: "grid", flex: "none", placeItems: "center" }}
        >
          <SpinnerIcon size={15} />
        </span>
      )}
      <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>
        {failed ? m.widget_error_chat_unavailable() : m.widget_loading_chat()}
      </span>
      {failed && (
        <button
          onClick={card.onRetry}
          style={{
            background: "transparent",
            border: 0,
            borderRadius: 9,
            color: theme.accent,
            cursor: "pointer",
            flex: "none",
            font: "inherit",
            fontWeight: 600,
            minHeight: 44,
            padding: "0 10px",
          }}
          type="button"
        >
          {m.widget_message_retry()}
        </button>
      )}
      <button
        aria-label={m.widget_header_close()}
        onClick={card.onClose}
        style={{ ...headerButtonStyle, flex: "none" }}
        type="button"
      >
        <CloseIcon size={15} />
      </button>
    </div>
  );
}
